import { Goal, ID, LifeArea, lifeAreaOtherID, Task } from '@griply/types';

export type GroupedTasks = {
  lifeAreaId: ID;
  lifeArea: LifeArea | null;
  tasks: Task[];
};

export type GroupTasksByLifeAreaOptions = {
  tasks: Task[];
  lifeAreas: LifeArea[];
  lifeAreaSorting?: ID[];
  /** When provided, resolves task.goalId → goal.lifeAreaId for tasks without a direct lifeAreaId. */
  goals?: Goal[];
};

/**
 * Recursively resolves a goal's life area by walking up the parent chain.
 * Matches the web's `getGoalLifeArea` logic in `use-life-area-tree.ts`.
 */
const getGoalLifeArea = (goal: Goal, allGoals: Goal[]): string | undefined => {
  if (goal.lifeAreaId || !goal.parentIds?.length) {
    return goal.lifeAreaId ?? undefined;
  }

  const parentGoal = allGoals.find((g) => g.id === goal.parentIds?.[0]);
  return parentGoal ? getGoalLifeArea(parentGoal, allGoals) : undefined;
};

export const groupTasksByLifeArea = (
  options: GroupTasksByLifeAreaOptions,
): GroupedTasks[] => {
  const { tasks, lifeAreas, lifeAreaSorting, goals } = options;

  // 1. Create Map of lifeAreaId -> Task[]
  const groupMap = new Map<string, Task[]>();

  // Initialize with all life areas
  for (const lifeArea of lifeAreas) {
    groupMap.set(lifeArea.id, []);
  }

  // Add "Other" section
  groupMap.set(lifeAreaOtherID, []);

  // 2. Assign tasks to groups
  // Priority: goal's life area (recursive) > task.lifeAreaId > "Other"
  for (const task of tasks) {
    let resolvedLifeAreaId: string | null = null;

    // First: resolve via goal hierarchy (matches web behavior)
    if (task.goalId && goals?.length) {
      const parentGoal = goals.find((g) => g.id === task.goalId);
      if (parentGoal) {
        resolvedLifeAreaId = getGoalLifeArea(parentGoal, goals) ?? null;
      }
    }

    // Fallback: use task's direct lifeAreaId
    if (!resolvedLifeAreaId) {
      resolvedLifeAreaId = task.lifeAreaId ?? null;
    }

    const targetId = resolvedLifeAreaId ?? lifeAreaOtherID;
    const group = groupMap.get(targetId);
    if (group) {
      group.push(task);
    }
  }

  // 3. Filter out empty groups
  const nonEmptyGroups: GroupedTasks[] = [];
  for (const [lifeAreaId, taskList] of groupMap.entries()) {
    if (!taskList.length) {
      continue;
    }

    const lifeArea = lifeAreas.find((la) => la.id === lifeAreaId) ?? null;
    nonEmptyGroups.push({
      lifeAreaId,
      lifeArea,
      tasks: taskList,
    });
  }

  // 4. Sort groups by lifeAreaSorting order
  if (lifeAreaSorting?.length) {
    nonEmptyGroups.sort((a, b) => {
      // "Other" always last
      if (a.lifeAreaId === lifeAreaOtherID) {
        return 1;
      }
      if (b.lifeAreaId === lifeAreaOtherID) {
        return -1;
      }

      const indexA = lifeAreaSorting.indexOf(a.lifeAreaId);
      const indexB = lifeAreaSorting.indexOf(b.lifeAreaId);

      // Items not in sorting array go to end (before "Other")
      if (indexA === -1 && indexB === -1) {
        return 0;
      }
      if (indexA === -1) {
        return 1;
      }
      if (indexB === -1) {
        return -1;
      }

      return indexA - indexB;
    });
  }

  return nonEmptyGroups;
};
